import React, { useEffect, useMemo, useState } from "react";
import { useCompanies, useCompanyDetails } from "@/hooks";
import { CompanyContext } from "./contexts";
import { Company } from "@/types/company";
import { defaultCompaniesFilters } from "@/hooks/companies/useCompanies";

interface CompanyProviderProps {
  children: React.ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  /* --------------------- States --------------------- */
  const [flagCompanyModalOpen, setFlagCompanyModalOpen] = useState(false);

  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyId, setCompanyId] = useState<Company["id"] | undefined>(
    undefined,
  );
  const [company, setCompany] = useState<Company | null>(null);

  /* --------------------- Hooks --------------------- */
  // TODO: create unit tests first before removing the following hook
  // because currently unsure why companies is in the dependencies below
  const {
    companies,
    loading: companiesLoading,
    error: companiesError,
  } = useCompanies({
    skip: !companyId || companyId === 0,
    filters: defaultCompaniesFilters,
  });

  const {
    company: companyDetails,
    loading: companyDetailsLoading,
    error: companyDetailsError,
  } = useCompanyDetails({ companyId });

  const closeCompanyDetailsModal = () => {
    setCompanyModalOpen(false);
    setCompanyId(undefined);
    setCompany(null);
  };

  /* --------------------- Constants --------------------- */
  const loading = companiesLoading || companyDetailsLoading;
  const error = companiesError || companyDetailsError;

  const closeFlagCompanyModal = () => {
    setFlagCompanyModalOpen(false);
  };

  /* --------------------- Effects --------------------- */
  useEffect(() => {
    if (companyId && companyDetails) {
      setCompany(companyDetails);
    } else {
      setCompany(null);
    }
  }, [companyId, companies]);

  const value = useMemo(
    () => ({
      companyModalOpen,
      setCompanyModalOpen,
      company,
      loading,
      error,
      setCompanyId,
      closeCompanyDetailsModal,
      flagCompanyModalOpen,
      setFlagCompanyModalOpen,
      closeFlagCompanyModal,
    }),
    [
      companyModalOpen,
      setCompanyModalOpen,
      company,
      loading,
      error,
      setCompanyId,
      flagCompanyModalOpen,
      setFlagCompanyModalOpen,
      closeFlagCompanyModal,
    ],
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}
